import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Divider,
  Avatar,
  Stack,
  Paper,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CheckCircle,
  BuildCircle,
  PendingActions,
  LocationOn,
  Business,
  AdminPanelSettings
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const WELL_STATUS = {
  "TS-04a": "active",
  "TS-04": "done",
  "TS-03D": "done",
  "TS-03C": "done",
  "TS-03": "done",
  "TS-03i": "done",
  "TS-03B": "done",
};

const WELL_DISABLED = {
  "TS-03D": false,
  "TS-03C": false,
  "TS-03": false,
  "TS-03i": false,
  "TS-03B": false,
  "TS-04": false,
  "TS-04a": false,
};

const STATUS_LABEL = {
  done: "Listo",
  active: "Activo",
  incoming: "Pendiente",
};

const drawerWidth = 280;

export default function ProjectSidebarLayout({ children }) {
  const [wells, setWells] = useState([]);
  const [selectedWell, setSelectedWell] = useState(null);
  const { t } = useTranslation();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  useEffect(() => {
    const token =
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/ocr-results-db`, {
        withCredentials: true,
        headers,
      })
      .then((res) => {
        const fromApi = res.data
          .map((r) => r.campos?.pozo)
          .filter(Boolean);

        const allWells = [...new Set([...Object.keys(WELL_STATUS), ...fromApi])];
        setWells(allWells);
      })
      .catch(console.error);
  }, []);

  const getStatus = (well) => WELL_STATUS[well] ?? "incoming";
  const isDisabled = (well) => WELL_DISABLED[well] ?? false;

  const getStatusConfig = (status) => {
    switch (status) {
      case "done":
        return {
          icon: <CheckCircle fontSize="small" />,
          label: t("project_sidebar.legend_done"),
          color: "success",
          dotColor: "#6ee7b7",
        };
      case "active":
        return {
          icon: <BuildCircle fontSize="small" />,
          label: t("project_sidebar.legend_active"),
          color: "info",
          dotColor: "#60a5fa",
        };
      default:
        return {
          icon: <PendingActions fontSize="small" />,
          label: t("project_sidebar.legend_incoming"),
          color: "default",
          dotColor: "rgba(255,255,255,0.25)",
        };
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar. */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
            background: "linear-gradient(180deg, #0a0f2e 0%, #111844 100%)",
            color: "#fff",
            borderRight: "none",
            boxShadow: "4px 0 24px rgba(0,0,0,0.3)",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
                width: 40,
                height: 40,
              }}
            >
              <Business fontSize="small" />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700, color: "#fff" }}>
                Alto Grande Lithium
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 1 }}>
            <LocationOn sx={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }} />
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 500 }}>
              Tacasal IV
            </Typography>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 2 }} />

        {/* Sección Pozos */}
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography
            variant="overline"
            sx={{
              color: "rgb(255, 255, 255)",
              fontWeight: 600,
              letterSpacing: 1.5,
              fontSize: 10,
              px: 1,
            }}
          >
            {t("project_sidebar.wells_section")}
          </Typography>
        </Box>

        {/* Lista de pozos */}
        <List sx={{ flex: 1, overflowY: "auto", px: 1, py: 0 }}>
          {wells.map((well) => {
            const status = getStatus(well);
            const statusConfig = getStatusConfig(status);
            const disabled = isDisabled(well);
            const isActive = location.pathname.includes(`/diario/${well}`);

            return (
              <ListItem key={well} disablePadding sx={{ mb: 0.5 }}>
             
                  <span style={{ width: "100%", display: "block" }}>
                    <ListItemButton
                      selected={isActive && !disabled}
                      onClick={() => {
                        if (disabled) return;
                        setSelectedWell(well);
                        navigate(`/panel/diario/${well}`);
                      }}
                      disabled={disabled}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        px: 2,
                        transition: "all 0.2s",
                        borderLeft: isActive && !disabled
                          ? `3px solid ${theme.palette.primary.main}`
                          : disabled
                            ? "3px solid rgba(100,116,139,0.3)"
                            : "3px solid transparent",
                        "&.Mui-disabled": {
                          opacity: 0.45,
                          filter: "grayscale(0.5)",
                          background: "rgba(30,30,50,0.3)",
                        },
                        "&.Mui-selected": {
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.18),
                          },
                        },
                        "&:hover": {
                          bgcolor: disabled
                            ? "rgba(30,30,50,0.3)"
                            : "rgba(255,255,255,0.04)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: disabled ? "rgba(100,116,139,0.5)" : statusConfig.dotColor,
                            boxShadow: disabled ? "none" : `0 0 8px ${statusConfig.dotColor}`,
                          }}
                        />
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: disabled ? "rgba(255,255,255,0.35)" : "#fff",
                            }}
                          >
                            {well}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{
                              color: disabled
                                ? "rgba(255,255,255,0.2)"
                                : "rgba(255,255,255,0.4)",
                            }}
                          >
                            {statusConfig.label}
                          </Typography>
                        }
                      />

                      <Chip
                        icon={statusConfig.icon}
                        label={t(`project_sidebar.legend_${status}`)}
                        size="small"
                        color={statusConfig.color}
                        variant="outlined"
                        sx={{
                          fontSize: 10,
                          height: 22,
                          "& .MuiChip-icon": { fontSize: 14, ml: 0.5 },
                          borderColor: alpha(theme.palette[statusConfig.color]?.main || "#fff", 0.3),
                          color: theme.palette[statusConfig.color]?.main || "#fff",
                          opacity: disabled ? 0.45 : 1,
                        }}
                      />
                    </ListItemButton>
                  </span>
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ p: 2, mt: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              bgcolor: alpha("#000", 0.2),
              borderRadius: 2,
              p: 2,
              border: `1px solid ${alpha("#fff", 0.05)}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 600, display: "block", mb: 1.5 }}
            >
              {t("project_sidebar.legend")}
            </Typography>
            <Stack spacing={1}>
              {[
                { status: "done",     color: "#6ee7b7",               label: t("project_sidebar.legend_done") },
                { status: "active",   color: "#60a5fa",               label: t("project_sidebar.legend_active") },
                { status: "incoming", color: "rgba(255,255,255,0.25)", label: t("project_sidebar.legend_incoming") },
              ].map((item) => (
                <Stack key={item.status} direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: item.color,
                      boxShadow: `0 0 6px ${item.color}`,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                    {item.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, bgcolor: "#0c1220", minHeight: "100vh" }}>
        {children}
      </Box>
    </Box>
  );
}